import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
var _excluded = ["align", "aria-label", "ariaLabel", "children", "className", "direction", "flipped", "focusTrap", "iconClass", "iconDescription", "id", "light", "menuOffset", "menuOffsetFlip", "menuOptionsClass", "onClick", "onClose", "onOpen", "open", "renderIcon", "selectorPrimaryFocus", "size", "innerRef"];
var _triggerButtonPositio, _triggerButtonPositio2;
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { forwardRef, useContext, useState, useRef, useEffect, useCallback, Children, isValidElement, cloneElement } from 'react';
import { OverflowMenuVertical } from '@carbon/icons-react';
import cx from 'classnames';
import invariant from 'invariant';
import { DIRECTION_TOP, DIRECTION_BOTTOM, FloatingMenu } from '../../internal/FloatingMenu.js';
import { ArrowUp, ArrowRight, ArrowDown, ArrowLeft, Escape } from '../../internal/keyboard/keys.js';
import { matches } from '../../internal/keyboard/match.js';
import { noopFn } from '../../internal/noopFn.js';
import { PrefixContext } from '../../internal/usePrefix.js';
import deprecate from '../../prop-types/deprecate.js';
import mergeRefs from '../../tools/mergeRefs.js';
import { setupGetInstanceId } from '../../tools/setupGetInstanceId.js';
import { IconButton } from '../IconButton/index.js';
import { useOutsideClick } from '../../internal/useOutsideClick.js';
import deprecateValuesWithin from '../../prop-types/deprecateValuesWithin.js';
import { mapPopoverAlign } from '../../tools/mapPopoverAlign.js';
var getInstanceId = setupGetInstanceId();
var on = function on(target) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  target.addEventListener.apply(target, args);
  return {
    release: function release() {
      target.removeEventListener.apply(target, args);
      return null;
    }
  };
};

/**
 * The CSS property names of the arrow keyed by the floating menu direction.
 */
var triggerButtonPositionProps = (_triggerButtonPositio = {}, _defineProperty(_triggerButtonPositio, DIRECTION_TOP, 'bottom'), _defineProperty(_triggerButtonPositio, DIRECTION_BOTTOM, 'top'), _triggerButtonPositio);

/**
 * Determines how the position of the arrow should affect the floating menu
 * position.
 */
var triggerButtonPositionFactors = (_triggerButtonPositio2 = {}, _defineProperty(_triggerButtonPositio2, DIRECTION_TOP, -2), _defineProperty(_triggerButtonPositio2, DIRECTION_BOTTOM, -1), _triggerButtonPositio2);

/**
 * Calculates the offset for the floating menu.
 *
 * @param menuBody - The menu body with the menu arrow.
 * @param direction - The floating menu direction.
 * @returns The adjustment of the floating menu position, upon the position of
 *          the menu arrow.
 */
var getMenuOffset = function getMenuOffset(menuBody, direction, trigger, flip) {
  var triggerButtonPositionProp = triggerButtonPositionProps[direction];
  var triggerButtonPositionFactor = triggerButtonPositionFactors[direction];
  if (process.env.NODE_ENV !== 'production') {
    !(triggerButtonPositionProp && triggerButtonPositionFactor) ? process.env.NODE_ENV !== "production" ? invariant(false, '[OverflowMenu] wrong floating menu direction: `%s`', direction) : invariant(false) : void 0;
  }
  var menuWidth = menuBody.offsetWidth,
    menuHeight = menuBody.offsetHeight;
  switch (triggerButtonPositionProp) {
    case 'top':
    case 'bottom':
      {
        // TODO: Ensure `trigger` is there for `<OverflowMenu open>`
        var triggerWidth = !trigger ? 0 : trigger.offsetWidth;
        return {
          left: (!flip ? 1 : -1) * (menuWidth / 2 - triggerWidth / 2),
          top: 0
        };
      }
    default:
      return {
        left: 0,
        top: 0
      };
  }
};
var OverflowMenu = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _cx, _cx2;
  var align = _ref.align,
    _ref$ariaLabel = _ref['aria-label'],
    ariaLabel = _ref$ariaLabel === void 0 ? null : _ref$ariaLabel,
    deprecatedAriaLabel = _ref.ariaLabel,
    children = _ref.children,
    className = _ref.className,
    _ref$direction = _ref.direction,
    direction = _ref$direction === void 0 ? DIRECTION_BOTTOM : _ref$direction,
    _ref$flipped = _ref.flipped,
    flipped = _ref$flipped === void 0 ? false : _ref$flipped,
    _ref$focusTrap = _ref.focusTrap,
    focusTrap = _ref$focusTrap === void 0 ? true : _ref$focusTrap,
    iconClass = _ref.iconClass,
    _ref$iconDescription = _ref.iconDescription,
    iconDescription = _ref$iconDescription === void 0 ? 'Options' : _ref$iconDescription,
    id = _ref.id,
    light = _ref.light,
    _ref$menuOffset = _ref.menuOffset,
    menuOffset = _ref$menuOffset === void 0 ? getMenuOffset : _ref$menuOffset,
    _ref$menuOffsetFlip = _ref.menuOffsetFlip,
    menuOffsetFlip = _ref$menuOffsetFlip === void 0 ? getMenuOffset : _ref$menuOffsetFlip,
    menuOptionsClass = _ref.menuOptionsClass,
    _ref$onClick = _ref.onClick,
    onClick = _ref$onClick === void 0 ? noopFn : _ref$onClick,
    _ref$onClose = _ref.onClose,
    onClose = _ref$onClose === void 0 ? noopFn : _ref$onClose,
    _ref$onOpen = _ref.onOpen,
    onOpen = _ref$onOpen === void 0 ? noopFn : _ref$onOpen,
    openProp = _ref.open,
    _ref$renderIcon = _ref.renderIcon,
    IconElement = _ref$renderIcon === void 0 ? OverflowMenuVertical : _ref$renderIcon,
    _ref$selectorPrimaryF = _ref.selectorPrimaryFocus,
    selectorPrimaryFocus = _ref$selectorPrimaryF === void 0 ? '[data-floating-menu-primary-focus]' : _ref$selectorPrimaryF,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'md' : _ref$size,
    innerRef = _ref.innerRef,
    other = _objectWithoutProperties(_ref, _excluded);
  var prefix = useContext(PrefixContext);
  var _useState = useState(openProp !== null && openProp !== void 0 ? openProp : false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    click = _useState4[0],
    setClick = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasMountedTrigger = _useState6[0],
    setHasMountedTrigger = _useState6[1];
  /**  The handle of `onfocusin` or `focus` event handler. */
  var hFocusIn = useRef(null);
  var instanceId = useRef(getInstanceId());
  var menuBodyRef = useRef(null);
  var menuItemRefs = useRef({});
  var prevOpenProp = useRef(openProp);
  var prevOpenState = useRef(open);
  /** The element ref of the tooltip's trigger button. */
  var triggerRef = useRef(null);
  var wrapperRef = useRef(null);

  // Sync open prop changes.
  useEffect(function () {
    if (prevOpenProp.current !== openProp) {
      setOpen(!!openProp);
      prevOpenProp.current = openProp;
    }
  }, [openProp]);

  // Mark trigger as mounted.
  useEffect(function () {
    if (triggerRef.current) {
      setHasMountedTrigger(true);
    }
  }, []);
  useEffect(function () {
    if (open && !prevOpenState.current) {
      onOpen();
    } else if (!open && prevOpenState.current) {
      onClose();
    }
    prevOpenState.current = open;
  }, [open, onClose, onOpen]);
  useOutsideClick(wrapperRef, function (_ref2) {
    var target = _ref2.target;
    if (open && (!menuBodyRef.current || target instanceof Node && !menuBodyRef.current.contains(target))) {
      closeMenu();
    }
  });
  var focusMenuEl = useCallback(function () {
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }, []);
  var closeMenu = useCallback(function (onCloseMenu) {
    setOpen(false);
    // Optional callback to be executed after the state as been set to close
    if (onCloseMenu) {
      onCloseMenu();
    }
    onClose();
  }, [onClose]);
  var closeMenuAndFocus = useCallback(function () {
    var wasClicked = click;
    var wasOpen = open;
    closeMenu(function () {
      if (wasOpen && !wasClicked) {
        focusMenuEl();
      }
    });
  }, [click, open, closeMenu, focusMenuEl]);
  var closeMenuOnEscape = useCallback(function () {
    var wasOpen = open;
    closeMenu(function () {
      if (wasOpen) {
        focusMenuEl();
      }
    });
  }, [open, closeMenu, focusMenuEl]);
  var handleClick = function handleClick(evt) {
    setClick(true);
    if (!menuBodyRef.current || !menuBodyRef.current.contains(evt.target)) {
      setOpen(function (prev) {
        return !prev;
      });
      onClick(evt);
    }
  };
  var handleKeyPress = function handleKeyPress(evt) {
    if (open && matches(evt, [ArrowUp, ArrowRight, ArrowDown, ArrowLeft])) {
      evt.preventDefault();
    }

    // Close the overflow menu on escape
    if (matches(evt, [Escape])) {
      closeMenuOnEscape();

      // Stop the esc keypress from bubbling out and closing something it shouldn't
      evt.stopPropagation();
    }
  };

  /**
   * Focuses the next enabled overflow menu item given the currently focused
   * item index and direction to move.
   */
  var handleOverflowMenuItemFocus = function handleOverflowMenuItemFocus(_ref3) {
    var _ref3$currentIndex = _ref3.currentIndex,
      currentIndex = _ref3$currentIndex === void 0 ? 0 : _ref3$currentIndex,
      direction = _ref3.direction;
    var enabledIndices = Children.toArray(children).reduce(function (acc, curr, i) {
      if (/*#__PURE__*/React.isValidElement(curr) && !curr.props.disabled) {
        acc.push(i);
      }
      return acc;
    }, []);
    var nextValidIndex = function () {
      var nextIndex = enabledIndices.indexOf(currentIndex) + direction;
      switch (nextIndex) {
        case -1:
          return enabledIndices.length - 1;
        case enabledIndices.length:
          return 0;
        default:
          return nextIndex;
      }
    }();
    var overflowMenuItem = menuItemRefs.current[enabledIndices[nextValidIndex]];
    overflowMenuItem === null || overflowMenuItem === void 0 ? void 0 : overflowMenuItem.focus();
  };
  var bindMenuBody = function bindMenuBody(menuBody) {
    if (!menuBody) {
      menuBodyRef.current = menuBody;
    }
    if (!menuBody && hFocusIn.current) {
      hFocusIn.current = hFocusIn.current.release();
    }
  };
  var handlePlace = function handlePlace(menuBody) {
    if (!menuBody) return;
    menuBodyRef.current = menuBody;
    var hasFocusin = 'onfocusin' in window;
    var focusinEventName = hasFocusin ? 'focusin' : 'focus';
    hFocusIn.current = on(menuBody.ownerDocument, focusinEventName, function (event) {
      var target = event.target;
      var triggerEl = triggerRef.current;
      if (typeof target.matches === 'function') {
        if (!menuBody.contains(target) && triggerEl && !target.matches(".".concat(prefix, "--overflow-menu:first-child, .").concat(prefix, "--overflow-menu-options:first-child"))) {
          closeMenuAndFocus();
        }
      }
    }, !hasFocusin);
  };
  var getTarget = function getTarget() {
    var triggerEl = triggerRef.current;
    if (triggerEl instanceof Element) {
      return triggerEl.closest('[data-floating-menu-container]') || document.body;
    }
    return document.body;
  };
  var menuBodyId = "overflow-menu-".concat(instanceId.current, "__menu-body");
  var overflowMenuClasses = cx(className, "".concat(prefix, "--overflow-menu"), (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--overflow-menu--open"), open), _defineProperty(_cx, "".concat(prefix, "--overflow-menu--light"), light), _defineProperty(_cx, "".concat(prefix, "--overflow-menu--").concat(size), size), _cx));
  var overflowMenuOptionsClasses = cx(menuOptionsClass, "".concat(prefix, "--overflow-menu-options"), (_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "--overflow-menu--flip"), flipped), _defineProperty(_cx2, "".concat(prefix, "--overflow-menu-options--open"), open), _defineProperty(_cx2, "".concat(prefix, "--overflow-menu-options--light"), light), _defineProperty(_cx2, "".concat(prefix, "--overflow-menu-options--").concat(size), size), _cx2));
  var overflowMenuIconClasses = cx("".concat(prefix, "--overflow-menu__icon"), iconClass);
  var childrenWithProps = Children.toArray(children).map(function (child, index) {
    if (/*#__PURE__*/isValidElement(child)) {
      var childElement = child;
      return /*#__PURE__*/cloneElement(childElement, {
        closeMenu: childElement.props.closeMenu || closeMenuAndFocus,
        handleOverflowMenuItemFocus: handleOverflowMenuItemFocus,
        ref: function ref(el) {
          menuItemRefs.current[index] = el;
        },
        index: index
      });
    }
    return null;
  });
  var menuBody = /*#__PURE__*/React.createElement("ul", {
    className: overflowMenuOptionsClasses,
    tabIndex: -1,
    role: "menu",
    "aria-label": ariaLabel || deprecatedAriaLabel,
    onKeyDown: handleKeyPress,
    id: menuBodyId
  }, childrenWithProps);
  var wrappedMenuBody = /*#__PURE__*/React.createElement(FloatingMenu, {
    focusTrap: focusTrap,
    triggerRef: triggerRef,
    menuDirection: direction,
    menuOffset: flipped ? menuOffsetFlip : menuOffset,
    menuRef: bindMenuBody,
    flipped: flipped,
    target: getTarget,
    onPlace: handlePlace,
    selectorPrimaryFocus: selectorPrimaryFocus
  }, /*#__PURE__*/cloneElement(menuBody, {
    'data-floating-menu-direction': direction
  }));
  var combinedRef = innerRef ? mergeRefs(triggerRef, innerRef, ref) : mergeRefs(triggerRef, ref);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "".concat(prefix, "--overflow-menu__wrapper"),
    "aria-owns": open ? menuBodyId : undefined,
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement(IconButton, _extends({}, other, {
    align: align,
    type: "button",
    "aria-haspopup": true,
    "aria-expanded": open,
    "aria-controls": open ? menuBodyId : undefined,
    className: overflowMenuClasses,
    onClick: handleClick,
    id: id,
    ref: combinedRef,
    size: size,
    label: iconDescription,
    kind: "ghost"
  }), /*#__PURE__*/React.createElement(IconElement, {
    className: overflowMenuIconClasses,
    "aria-label": iconDescription
  })), open && hasMountedTrigger && wrappedMenuBody));
});
export { OverflowMenu, OverflowMenu as default, getMenuOffset };